import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Merge, Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { partnerName, stageLabel } from "./crmConst";

export const MergeDialog = ({ open, onOpenChange, client, partners, onMerged }) => {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setSrc(null); setQ(""); api.get("/crm/clients").then((r) => setAll(r.data)); } }, [open]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return all.filter((c) => c.id !== client?.id && (!s || [c.company, c.contact_name, c.email, c.city].some((v) => (v || "").toLowerCase().includes(s))));
  }, [all, q, client]);
  const merge = async () => {
    setBusy(true);
    try {
      await api.post(`/crm/clients/${client.id}/merge`, { source_id: src.id });
      toast.success(`« ${src.company} » fusionné dans « ${client.company} »`);
      onMerged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fusion impossible");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid="crm-merge-dialog">
        <DialogHeader>
          <DialogTitle>Fusionner un doublon dans {client?.company}</DialogTitle>
          <DialogDescription>Les projets, l'historique et les tâches du doublon seront rattachés à « {client?.company} », ses coordonnées complèteront les champs vides, puis le doublon sera supprimé.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder="Rechercher le doublon…" data-testid="crm-merge-search" />
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1.5">
          {list.map((c) => (
            <button key={c.id} type="button" onClick={() => setSrc(c)} data-testid={`crm-merge-row-${c.id}`}
              className={`w-full text-left rounded-lg border px-3 py-2 transition-[border-color,background-color] ${src?.id === c.id ? "border-genis bg-genis/5" : "border-border hover:border-genis/50"}`}>
              <div className="font-semibold text-sm">{c.company}</div>
              <div className="text-xs text-muted-foreground">{partnerName(partners, c.partner_id)} · {stageLabel(c.stage)} · {c.project_count} projet(s){c.city ? ` · ${c.city}` : ""}</div>
            </button>
          ))}
          {!list.length && <p className="text-sm text-muted-foreground py-6 text-center">Aucun autre client.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button className="bg-genis hover:bg-genis-dark text-white" disabled={!src || busy} onClick={merge} data-testid="crm-merge-confirm">
            <Merge className="h-4 w-4 mr-1" /> Fusionner{src ? ` « ${src.company} »` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
